import { ChatCircle } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { AnimatedImage } from "@/components/ui/animated-image";
import { formatRelativeTime } from "@/lib/time-utils";

interface ThreadReplyPreviewProps {
  messageId: string;
  threadReplyCount: number | null | undefined;
  threadReplyUsers: string[] | null | undefined;
  threadLatestReplyAt: number | null | undefined;
  /** True when this message is the source of a thread (system
   *  thread-created row exists AND `reply_count > 0`). Inline replies
   *  alone do not flip this — they live in the channel timeline as
   *  separate rows and don't get a badge on the source. */
  threadActive: boolean;
  userMap?: Map<
    string,
    { id: string; display_name: string; avatar_url?: string | null }
  >;
  onThreadClick?: (messageId: string) => void;
}

/**
 * Stacked-avatars + count + relative-time badge rendered beneath the
 * message body when this message is the source of a thread (i.e. the
 * "Create Thread" affordance has been used AND the thread has at least
 * one real reply). Renders nothing when `threadActive` is false —
 * inline replies alone do NOT trigger this badge; they show as their
 * own rows in the channel timeline with citation pills, and need no
 * back-pointer on the source.
 */
export function ThreadReplyPreview({
  messageId,
  threadReplyCount,
  threadReplyUsers,
  threadLatestReplyAt,
  threadActive,
  userMap,
  onThreadClick,
}: ThreadReplyPreviewProps) {
  if (!threadActive) return null;
  if (threadReplyCount == null || threadReplyCount === 0) return null;
  return (
    <Button
      type="button"
      variant="outline"
      onClick={() => onThreadClick?.(messageId)}
      className="group/thread mt-2 inline-flex h-auto items-center gap-2 rounded-md border-info/20 bg-info/[0.06] px-2 py-1 text-xs text-info hover:border-info/30 hover:bg-info/[0.1]"
    >
      {threadReplyUsers && threadReplyUsers.length > 0 && userMap && (
        <div className="flex -space-x-1.5">
          {threadReplyUsers.slice(0, 3).map((userId, idx) => {
            const replyUser = userMap.get(userId);
            return (
              <div
                key={userId}
                className="flex h-5 w-5 items-center justify-center overflow-hidden rounded-full bg-foreground/20 text-[8px] font-semibold ring-2 ring-background"
                style={{ zIndex: 3 - idx }}
              >
                {replyUser?.avatar_url ? (
                  <AnimatedImage
                    src={replyUser.avatar_url}
                    alt={replyUser.display_name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  (replyUser?.display_name || "?").slice(0, 2).toUpperCase()
                )}
              </div>
            );
          })}
        </div>
      )}
      <span className="flex items-center gap-1 font-medium group-hover/thread:underline">
        <ChatCircle size={12} weight="Bold" />
        Thread • {threadReplyCount}{" "}
        {threadReplyCount === 1 ? "message" : "messages"}
      </span>
      {threadLatestReplyAt && (
        <span className="text-muted-foreground/80">
          {formatRelativeTime(threadLatestReplyAt)}
        </span>
      )}
    </Button>
  );
}
